<script setup lang="ts">
/**
 * LensSwitcher — perspective chip + dropdown.
 *
 * Ported from the prototype's #lens-switcher pattern (lines 7340-
 * 7374 of streama-cms.html). State lives in the lens Pinia store;
 * this component only renders and dispatches change events.
 *
 * Uses Reka UI's Popover for accessibility (focus management, ESC
 * to close, click-outside-to-close).
 */
import { useLensStore, type LensId } from '@/stores/lens'
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui'
import { ChevronDown, Check } from 'lucide-vue-next'
import { useToastStore } from '@/stores/toast'
import { useI18n } from 'vue-i18n'

const lens = useLensStore()
const toast = useToastStore()
const { t } = useI18n()

function pick(id: LensId) {
  if (id === lens.currentId) return
  lens.setLens(id)
  toast.push({
    kind: 'info',
    message: t('lens.switched', { name: lens.current.name }),
  })
}
</script>

<template>
  <PopoverRoot>
    <PopoverTrigger
      class="inline-flex items-center gap-2 h-8 px-2.5 text-xs font-medium
             bg-surface-2 hover:bg-surface-3 text-text-2 hover:text-text-1
             border border-border hover:border-border-strong rounded
             transition-colors focus-visible:outline-none focus-visible:shadow-focus"
    >
      <span
        class="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider
               bg-brand-soft text-brand-deep rounded-sm"
      >{{ lens.current.label }}</span>
      <span class="text-text-3">{{ lens.current.name }}</span>
      <ChevronDown :size="12" class="text-text-4" />
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        :side-offset="6"
        align="end"
        class="w-72 p-1.5 bg-surface border border-border-strong rounded-lg
               shadow-lg z-50 outline-none"
      >
        <p class="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-text-4">
          {{ $t('lens.choose') }}
        </p>
        <button
          v-for="l in lens.allLenses"
          :key="l.id"
          type="button"
          :class="[
            'w-full flex items-start gap-2.5 px-2 py-2 text-left rounded transition-colors',
            'hover:bg-surface-2',
            l.id === lens.currentId ? 'bg-surface-2' : '',
          ]"
          @click="pick(l.id)"
        >
          <Check
            :size="14"
            :class="[
              'mt-0.5 shrink-0',
              l.id === lens.currentId ? 'text-brand' : 'text-transparent',
            ]"
          />
          <span class="flex-1 min-w-0">
            <span class="block text-xs font-medium text-text-1">{{ l.name }}</span>
            <span class="block text-[10px] text-text-4 mt-0.5">
              {{ l.label }}
            </span>
          </span>
        </button>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>
